import { Component, EventEmitter, HostListener, Input, Output } from "@angular/core";
import { Router } from "@angular/router";
import { createSelector, Store } from "@ngrx/store";
import { FreyaCommonModule } from "src/app/freya-common/freya-common.module";
import {  JobCreationFormAction, LocationType, requiredFieldsValidationMessages } from "src/app/global.constants";
import { FreyaNotificationsService } from "src/app/services/freya-notifications.service";
import { isLocationInvalid } from "src/app/utilities/locations.util";

import { createConfigSelector } from '../../../state/branding.store';
import { EMAIL_PATTERN, PHONE_PATTERN } from "../../job-helpers";
import { overviewLocationsFeature } from '../../job-overview/overview-locations/overview-locations.feature';
import { LocationEditorState } from '../../job-overview/overview-locations/overview-locations.state';
import { jobToolFeature, selectJobLoading } from "../../job-tool.reducer";
import { jobCreateCustomerFeature } from "../job-create-customer/job-create-customer.feature";

import { JobFormActionButtonActions } from "./job-form-action-button.actions";

export const selectHasLocationMissingField = (field: keyof LocationEditorState) => createSelector(
  overviewLocationsFeature.selectLocationEditors,
  (locationEditors) => {

      const locationWithFields = [
          'start',
          'end',
      ] as LocationType[];

      for (const locationType of locationWithFields) {

          const location = locationEditors[locationType];

          if (!location) { continue; }

          if (!location.selectedLocation) { continue; }

          if (!location[field]) { return true }
      }

      return false;
  }
);

const selectNeedsBedrooms = createSelector(
    createConfigSelector('JOBS_BEDROOMSMANDATORY'),
    selectHasLocationMissingField('size'),
    (bedroomsMandatory, hasLocationMissingBedrooms) => bedroomsMandatory && hasLocationMissingBedrooms,
);

const selectNeedsBuildingType = createSelector(
    createConfigSelector('JOBS_BEDROOMSMANDATORY'),
    selectHasLocationMissingField('buildingType'),
    (buildingTypeMandatory, hasLocationMissingBuildingType) => buildingTypeMandatory && hasLocationMissingBuildingType,
)

export const selectButtonDisabledMessage = createSelector(
    createConfigSelector('JOBS_HOWHEARDMANDATORY'),
    selectNeedsBedrooms,
    selectNeedsBuildingType,
    overviewLocationsFeature.selectLocationEditors,
    jobCreateCustomerFeature.selectCustomerEditor,
    (
        howHeardMandatory,
        needsBedrooms,
        needsBuildingType,
        locationEditors,
        {
            fullName,
            referralSource,
            email,
            phone,
        },
    ) => {

        if (!fullName) {
            return 'A customer name is required to create this job.';
        }

        const hasInvalidLocations = Object.keys(locationEditors).some((k) => isLocationInvalid(locationEditors[k]));

        if (hasInvalidLocations) {
            return 'Job locations are invalid.';
        }

        if (howHeardMandatory && !referralSource) {
            return 'A referral source is required to create this job.';
        }

        if (email && !EMAIL_PATTERN.test(email)) {
            return 'Invalid email format.';
        }

        if (phone && !PHONE_PATTERN.test(phone)) {
            return 'Invalid phone format';
        }

        if (needsBedrooms) {
            return requiredFieldsValidationMessages.bedrooms;
        }

        if (needsBuildingType) {
            return requiredFieldsValidationMessages.dwellingType;
        }
    }
);

export const selectButtonDisabled = createSelector(selectButtonDisabledMessage, Boolean);

@Component({
    selector: 'app-form-action-button',
    standalone: true,
    imports: [
        FreyaCommonModule,
    ],
    templateUrl: './job-form-action-button.html',
    styleUrls: ['./job-form-action-button.scss']
})
export class JobFormActionButtonComponent {

    @Input() action: JobCreationFormAction;
    @Output() buttonClicked = new EventEmitter();

    @HostListener('window:keydown.control.s', ['$event'])
    onControlS(event: KeyboardEvent) {
        event.preventDefault();
        this.store.dispatch(this.actions.createJobShortcutEntered())
    }

    @HostListener('window:keydown.control.shift.s', ['$event'])
    onControlShiftS(event: KeyboardEvent) {
        event.preventDefault();
        this.store.dispatch(this.actions.createAndCloseJobButtonClicked())
    }

    viewModel$ = this.store.select(createSelector(
      selectButtonDisabledMessage,
      jobToolFeature.selectNewJobMutating,
      selectJobLoading,
      (disabledMessage, jobCreating, jobUpdating) => {

        const isCreateButton = this.action === 'create' || this.action === 'create-close';

        const isSaving = isCreateButton ? jobCreating : jobUpdating;

        const viewModel: {
          label: string;
          icon: 'pi pi-check' | 'pi pi-spin pi-spinner';
          disabled: boolean;
          tooltip?: string;
        } = {
          label: this._getLabel({ isSaving }),
          icon: 'pi pi-check',
          disabled: false,
        }

        if (isSaving) {
          viewModel.icon = 'pi pi-spin pi-spinner';
          viewModel.disabled = true;
        } else if (disabledMessage) {
          viewModel.icon = 'pi pi-check';
          viewModel.disabled = true;
          viewModel.tooltip = disabledMessage;
        }

        return viewModel;
      }
    ));

    public actions = JobFormActionButtonActions;

    constructor(
        public store: Store,
        public localNotify: FreyaNotificationsService,
        public router: Router
    ) { }

    _getLabel({ isSaving }: { isSaving: boolean }) {

      const isCreateButton = this.action === 'create' || this.action === 'create-close';

      if (isSaving && isCreateButton) {
        return 'Creating job...';
      } else if (isSaving) {
        return 'Updating job...';
      }

      switch (this.action) {
        case 'create':
          return 'Create Job';
        case 'create-close':
          return 'Create & Close';
        case 'update':
          return 'Update Job';
        case 'update-close':
          return 'Close Job';
      }
    }

}
